/**
 * AppIdContext
 * URLパスから現在のアプリIDを管理するコンテキスト
 */
import React, { createContext, useContext, ReactNode } from 'react';
import { useParams, useLocation } from 'react-router-dom';

interface AppIdContextType {
  appId: string;
  isValidApp: boolean;
}

const AppIdContext = createContext<AppIdContextType | undefined>(undefined);

// サポートされているアプリのリスト
export const SUPPORTED_APPS = ['chusho-app', 'ghouseishosi'] as const;
export type AppId = typeof SUPPORTED_APPS[number];

export const APP_METADATA: Record<AppId, { name: string; nameEn: string; category: string }> = {
  'chusho-app': {
    name: '中小企業診断士',
    nameEn: 'Small and Medium Enterprise Management Consultant',
    category: 'business'
  },
  'ghouseishosi': {
    name: '行政書士',
    nameEn: 'Administrative Scrivener',
    category: 'legal'
  }
};

export function AppIdProvider({ children }: { children: ReactNode }) {
  const { appId: urlAppId } = useParams<{ appId: string }>();
  const location = useLocation();

  // URLパスからappIdを取得（ルートパスの場合は空文字）
  let appId = urlAppId || '';

  // ルートパス以外でappIdが無い場合、パスから推定
  if (!appId && location.pathname !== '/') {
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts.length > 0) {
      appId = pathParts[0];
    }
  }

  const isValidApp = SUPPORTED_APPS.includes(appId as AppId);

  return (
    <AppIdContext.Provider value={{ appId, isValidApp }}>
      {children}
    </AppIdContext.Provider>
  );
}

export function useAppId(): AppIdContextType {
  const context = useContext(AppIdContext);
  if (context === undefined) {
    throw new Error('useAppId must be used within AppIdProvider');
  }
  return context;
}
